import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { trackPageView } from './lib/analytics';
import LandingPage from './LandingPage';
import PackageSelection from './PackageSelection';
import NewPhotoshootBooking from './NewPhotoshootBooking';
import EventBooking from './EventBooking';
import AdminDashboard from './AdminDashboard';
import EventsList from './EventsList';
import EventDetails from './EventDetails';
import PhotoshootBooking from './PhotoshootBooking'; // Using the new one we fixed
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';
import CancellationPolicy from './CancellationPolicy';
import PaymentSuccess from './PaymentSuccess';
import PaymentFailure from './PaymentFailure';
import TestReceipt from './TestReceipt';
import axios, { API_BASE_URL } from './apiConfig';
import './App.css';

// react-router navigations are client-side, so GA4's automatic page_view (fired once
// at script load) never sees route changes — log one explicitly on every navigation.
// The very first mount is skipped since Firebase Analytics already logs an automatic
// page_view for the initial load; without the skip, the landing page would double-count.
function AnalyticsPageViewTracker() {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  const [siteBanner, setSiteBanner] = useState({
    enabled: true,
    message: 'Revised prices will be effective from 1 November 2026.',
  });

  useEffect(() => {
    let isActive = true;

    const fetchSiteBanner = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/admin/settings/public?id=global`);
        const banner = data?.settings?.site_banner;
        if (isActive && banner && typeof banner === 'object') {
          setSiteBanner({
            enabled: banner.enabled !== false,
            message: typeof banner.message === 'string' ? banner.message : '',
          });
        }
      } catch (error) {
        console.error('Failed to load site banner settings', error);
      }
    };

    fetchSiteBanner();
    return () => { isActive = false; };
  }, []);

  return (
    <Router>
      <div className="min-h-screen">
        <AnalyticsPageViewTracker />
        {siteBanner.enabled && siteBanner.message.trim() && (
          <div className="price-revision-banner" role="status">
            {siteBanner.message}
          </div>
        )}
        <Routes>
          <Route path="/" element={<LandingPage />} />
          {/* <Route path="/packages" element={<PackageSelection />} /> */}
          <Route path="/booking" element={<NewPhotoshootBooking />} />
          <Route path="/event" element={<EventBooking />} />
          <Route path="/events" element={<EventsList />} />
          <Route path="/events/:id" element={<EventDetails />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cancellation" element={<CancellationPolicy />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/failure" element={<PaymentFailure />} />
          <Route path="/test-receipt" element={<TestReceipt />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#471F21',
              color: '#F4ECDE',
            },
          }}
        />
      </div>
    </Router>
  );
}

export default App;
